import React, { useEffect, useState } from 'react'
import { Divider, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material'
import { useField, useFormikContext } from 'formik'
import { J2Launchapp } from '@e3dc-react/icons'
import { makeStyles } from 'tss-react/mui'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useNavigate, useParams } from 'react-router-dom'
import { Autocomplete } from 'src/Libs/inputs/formik'
import { useFilteredCompanyOptions } from '../hooks/use-filtered-company-options'
import { CompanyOption } from '../models'

interface CompanyAutocompleteWithCreationProps {
  name: string
  maxDisplayedResultCount?: number
  withTrim?: boolean
}

const useStyles = makeStyles()(theme => ({
  listItemText: {
    '&.MuiListItemText-primary': {
      fontWeight: 'normal',
    },
  },
  listItem: {
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
  },
  listIcon: {
    '&.MuiListItemIcon-root svg': {
      fontSize: 24,
    },
  },
}))

/**
 * Input field for company names that asynchroniously fetches data to suggest names that contain the input value but also allow new values.
 */
const CompanyAutocompleteWithCreation: React.FC<CompanyAutocompleteWithCreationProps> = ({ maxDisplayedResultCount, name, withTrim }) => {
  const { classes } = useStyles()
  const { t } = useCustomTranslation()
  const navigate = useNavigate()
  const { id: companyId } = useParams()
  const { fetchFilteredCompanies } = useFilteredCompanyOptions()
  const { setFieldValue } = useFormikContext()

  const [options, setOptions] = useState<CompanyOption[]>([])
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  const [field, meta] = useField(name)

  const loadCompanyOptions = async (value: string): Promise<void> => {
    if (!value || value.length < 3 || meta.initialValue === value) {
      setOptions([])
      setIsMenuOpen(false)
      return
    }

    setLoading(true)

    try {
      const companyOptions = await fetchFilteredCompanies(value, maxDisplayedResultCount)
      setOptions(companyOptions)
      setLoading(false)
    } catch (error) {
      setIsMenuOpen(false)
      setLoading(false)
    }
  }

  useEffect(() => {
    if (options.length > 0) {
      setIsMenuOpen(true)
    } else {
      setIsMenuOpen(false)
    }
  }, [options]) //eslint-disable-line

  const handleChange = (value: string | CompanyOption | null): void => {
    if (typeof value === 'string' || value === null) {
      setFieldValue(name, value)
    } else {
      onOptionClick(value)
    }
  }

  const handleInputChange = (event: React.SyntheticEvent | null, value: string): void => {
    setFieldValue(name, value)
    loadCompanyOptions(value)
  }

  const onOptionClick = (option: CompanyOption): void => {
    if (option.value.toString() === companyId) {
      setFieldValue(name, meta.initialValue)
    } else {
      navigate(`/overview/companies/${option.value}`)
    }
  }

  return (
    <Autocomplete<string>
      name={name}
      label={t('common.companyName')}
      value={field.value}
      allowValueCreation
      handleInputChange={handleInputChange}
      handleChange={handleChange}
      options={options}
      isOptionEqualToValue={(option, value) => option.inputLabel === value?.inputLabel}
      maxDisplayedResultCount={maxDisplayedResultCount}
      groupBy={options => 'group'}
      isMenuOpen={isMenuOpen}
      onClose={() => setIsMenuOpen(false)}
      loading={loading}
      getOptionLabel={option => (typeof option === 'string' ? option : option.inputLabel)}
      renderGroup={params => (
        <>
          <>
            <ListItem key={params.key}>
              <ListItemText primary={t('service-dashboard.company.name-suggestions.title')} />
            </ListItem>
            <Divider sx={theme => ({ borderColor: theme.palette.shades.shade03 })} />
          </>
          {params.children}
        </>
      )}
      renderOption={(props, option) => (
        <ListItem {...props} sx={{ p: '0px !important' }}>
          <ListItemButton className={classes.listItem} onClick={() => onOptionClick(option)}>
            <ListItemText primary={option.optionLabel} classes={{ primary: classes.listItemText }} />
            <ListItemIcon className={classes.listIcon}>
              <J2Launchapp fontSize={'small'} />
            </ListItemIcon>
          </ListItemButton>
        </ListItem>
      )}
      withTrim={withTrim}
    />
  )
}

export default CompanyAutocompleteWithCreation
